import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { locationsService } from '../../../services/locationsService';

const WEEKDAYS = [
  { idx: 0, label: 'Pn', full: 'Poniedziałek' },
  { idx: 1, label: 'Wt', full: 'Wtorek' },
  { idx: 2, label: 'Śr', full: 'Środa' },
  { idx: 3, label: 'Cz', full: 'Czwartek' },
  { idx: 4, label: 'Pt', full: 'Piątek' },
  { idx: 5, label: 'Sb', full: 'Sobota' },
  { idx: 6, label: 'Nd', full: 'Niedziela' },
];

const SalesByHourTab = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const [dateFrom, setDateFrom] = useState(toISO(firstDay));
  const [dateTo, setDateTo] = useState(toISO(now));
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [intervalMin, setIntervalMin] = useState(30);
  const [splitByLocation, setSplitByLocation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ by_interval: [], by_location_interval: [], totals: {}, count: 0 });

  useEffect(() => {
    locationsService.getLocations().then(res => {
      const arr = res?.data?.locations || res?.locations || res?.data || [];
      setLocations(Array.isArray(arr) ? arr : []);
    }).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = {
      date_from: dateFrom,
      date_to: dateTo,
      weekdays: weekdays.join(','),
      interval_min: intervalMin,
    };
    if (locationId) {
      p.location_id = locationId;
    } else {
      p.location_id = 'all';
      p.all_locations = '1';
      if (splitByLocation) p.split_by_location = '1';
    }
    return p;
  }, [dateFrom, dateTo, locationId, weekdays, intervalMin, splitByLocation]);

  const loadReport = useCallback(async () => {
    if (weekdays.length === 0) {
      toast.error('Wybierz przynajmniej jeden dzień tygodnia');
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/admin/sales-by-hour', { params: buildParams() });
      if (response.data?.success) {
        setData(response.data.data || { by_interval: [], by_location_interval: [], totals: {}, count: 0 });
      } else {
        toast.error(response.data?.error || 'Błąd pobierania danych');
      }
    } catch (err) {
      console.error('[SalesByHour]', err);
      toast.error(err?.message || 'Błąd');
    } finally {
      setLoading(false);
    }
  }, [buildParams, weekdays.length]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const quickRange = (type) => {
    const d = new Date();
    if (type === 'today') {
      setDateFrom(toISO(d)); setDateTo(toISO(d));
    } else if (type === 'month') {
      setDateFrom(toISO(new Date(d.getFullYear(), d.getMonth(), 1))); setDateTo(toISO(d));
    } else if (type === 'prev_month') {
      const f = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const t = new Date(d.getFullYear(), d.getMonth(), 0);
      setDateFrom(toISO(f)); setDateTo(toISO(t));
    } else if (type === 'year') {
      setDateFrom(toISO(new Date(d.getFullYear(), 0, 1))); setDateTo(toISO(d));
    }
  };

  const toggleWeekday = (idx) => {
    setWeekdays(prev => prev.includes(idx) ? prev.filter(x => x !== idx) : [...prev, idx].sort());
  };

  const quickWeekdays = (preset) => {
    if (preset === 'all') setWeekdays([0, 1, 2, 3, 4, 5, 6]);
    else if (preset === 'workdays') setWeekdays([0, 1, 2, 3, 4]);
    else if (preset === 'weekend') setWeekdays([5, 6]);
    else if (preset === 'saturday') setWeekdays([5]);
    else if (preset === 'sunday') setWeekdays([6]);
    else if (preset === 'none') setWeekdays([]);
  };

  const totals = data.totals || {};
  const fmt = (n) => Number(n || 0).toFixed(2);

  // Maksimum dla wizualizacji slupkow
  const maxBrutto = Math.max(...(data.by_interval || []).map(x => x.brutto || 0), 1);

  return (
    <div style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>📊 Sprzedaż wg godzin</h3>

      {/* Filtry */}
      <div style={{
        display: 'flex', gap: '1rem', alignItems: 'end', marginBottom: '1rem',
        padding: '1rem', backgroundColor: 'var(--bg)', borderRadius: 4,
        border: '1px solid var(--border)', flexWrap: 'wrap',
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Data od</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Data do</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Szybki zakres</label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[['today','Dziś'],['month','Ten mies.'],['prev_month','Poprz. mies.'],['year','Rok']].map(([k,l])=>(
              <button key={k} onClick={() => quickRange(k)} style={{
                padding: '6px 12px', fontSize: '12px',
                border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer'
              }}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Lokalizacja</label>
          <select value={locationId} onChange={e => setLocationId(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', maxWidth: '100%' }}>
            <option value="">Wszystkie lokalizacje</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.nazwa} ({l.kod_lokalizacji})</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Interwał</label>
          <select value={intervalMin} onChange={e => setIntervalMin(Number(e.target.value))}
            style={{ padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}>
            <option value={15}>15 minut</option>
            <option value={30}>30 minut</option>
            <option value={60}>1 godzina</option>
          </select>
        </div>
        {!locationId && (
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>Per lokalizacja</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.6rem', fontSize: 12 }}>
              <input type="checkbox" checked={splitByLocation} onChange={e => setSplitByLocation(e.target.checked)} />
              Rozbij na lokalizacje
            </label>
          </div>
        )}
        <button onClick={loadReport} disabled={loading} style={{
          padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--info)',
          color: '#fff', border: 'none', cursor: 'pointer'
        }}>🔄 Odśwież</button>
      </div>

      {/* Dni tygodnia */}
      <div style={{
        display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem',
        padding: '0.75rem 1rem', backgroundColor: 'var(--bg)', borderRadius: 4,
        border: '1px solid var(--border)', flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>Dni tygodnia:</span>
        {WEEKDAYS.map(w => (
          <label key={w.idx} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
            backgroundColor: weekdays.includes(w.idx) ? 'var(--info)' : 'var(--card-bg)',
            color: weekdays.includes(w.idx) ? '#fff' : 'var(--text-secondary)',
            fontWeight: weekdays.includes(w.idx) ? 600 : 400,
            border: '1px solid var(--border)', fontSize: '11px',
          }} title={w.full}>
            <input type="checkbox"
              checked={weekdays.includes(w.idx)}
              onChange={() => toggleWeekday(w.idx)}
              style={{ display: 'none' }} />
            {w.label}
          </label>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {[['all','Wszystkie'],['workdays','Robocze'],['weekend','Weekend'],['saturday','Tylko Sb'],['sunday','Tylko Nd'],['none','Wyczyść']].map(([k,l])=>(
            <button key={k} onClick={() => quickWeekdays(k)} style={{
              padding: '4px 10px', fontSize: '11px',
              border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer'
            }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Karty totali */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '0.75rem', marginBottom: '1rem'
      }}>
        {[
          { label: 'Paragonów', val: totals.paragony, color: 'var(--primary)', raw: true },
          { label: 'Obrót brutto', val: totals.brutto, color: 'var(--success)' },
          { label: 'Obrót netto', val: totals.netto, color: 'var(--success)' },
          { label: 'Koszt towaru', val: totals.koszt, color: 'var(--warning)' },
          { label: 'Marża', val: totals.marza, color: 'var(--success)' },
          { label: 'Marża %', val: totals.marza_proc, color: 'var(--cyan)', suffix: '%' },
          { label: 'Średnia paragonu', val: totals.srednia, color: 'var(--purple, #6f42c1)' },
        ].map((c, i) => (
          <div key={i} style={{
            padding: '0.75rem', backgroundColor: 'var(--bg)', borderRadius: 4,
            border: '1px solid var(--border)', borderLeft: `3px solid ${c.color}`
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: c.color }}>
              {c.raw ? c.val : fmt(c.val)}{c.suffix || (c.raw ? '' : ' zł')}
            </div>
          </div>
        ))}
      </div>

      {/* Tabela main */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ładowanie...</div>
      ) : data.count === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Brak transakcji w wybranym zakresie/filtrach
        </div>
      ) : (
        <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Przedział</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Paragony</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót brutto</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'left', width: '20%' }}>Wizualizacja</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Obrót netto</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Koszt</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża %</th>
                <th className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Śr. paragon</th>
              </tr>
            </thead>
            <tbody>
              {data.by_interval.map((r, i) => {
                const pct = maxBrutto > 0 ? (r.brutto / maxBrutto) * 100 : 0;
                return (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.5rem', fontWeight: 600, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.time_slot}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.paragony}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(r.brutto)}</td>
                    <td className="col-hide-mobile" style={{ padding: '0.5rem' }}>
                      <div style={{
                        height: 10, backgroundColor: 'var(--border)', borderRadius: 2,
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`, height: '100%',
                          backgroundColor: 'var(--success)',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </td>
                    <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.netto)}</td>
                    <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--warning)', whiteSpace: 'nowrap' }}>{fmt(r.koszt)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: r.marza > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                      {fmt(r.marza)}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.marza_proc)}%</td>
                    <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--purple, #6f42c1)', whiteSpace: 'nowrap' }}>{fmt(r.srednia)}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '2px solid var(--border)', backgroundColor: 'var(--bg)', fontWeight: 700 }}>
                <td style={{ padding: '0.5rem' }}>TOTAL</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{totals.paragony}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.brutto)}</td>
                <td className="col-hide-mobile" />
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.netto)}</td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--warning)', whiteSpace: 'nowrap' }}>{fmt(totals.koszt)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: totals.marza > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                  {fmt(totals.marza)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(totals.marza_proc)}%</td>
                <td className="col-hide-mobile" style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--purple, #6f42c1)', whiteSpace: 'nowrap' }}>{fmt(totals.srednia)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Per lokalizacja */}
      {data.split_by_location && Array.isArray(data.by_location_interval) && data.by_location_interval.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h4 style={{ margin: '0 0 0.75rem', fontSize: 14 }}>📍 Rozbicie per lokalizacja</h4>
          {data.by_location_interval.map((loc) => (
            <div key={loc.location_id} style={{ marginBottom: '1.5rem' }}>
              <div style={{
                padding: '0.5rem 0.75rem', backgroundColor: 'var(--info)', color: '#fff',
                fontWeight: 600, fontSize: 13, borderRadius: '4px 4px 0 0'
              }}>
                {loc.location_nazwa} ({loc.location_kod})
              </div>
              <div className="admin-table-wrap" style={{ overflowX: 'auto', border: '1px solid var(--border)', borderTop: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '0.4rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Przedział</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Paragony</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Brutto</th>
                      <th style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża</th>
                      <th className="col-hide-mobile" style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Marża %</th>
                      <th className="col-hide-mobile" style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>Śr. paragon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loc.intervals.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '0.4rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.time_slot}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.paragony}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.brutto)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right', color: r.marza > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                          {fmt(r.marza)}
                        </td>
                        <td className="col-hide-mobile" style={{ padding: '0.4rem', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(r.marza_proc)}%</td>
                        <td className="col-hide-mobile" style={{ padding: '0.4rem', textAlign: 'right', color: 'var(--purple, #6f42c1)', whiteSpace: 'nowrap' }}>{fmt(r.srednia)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: '0.75rem', fontSize: 11, color: 'var(--text-muted)' }}>
        Agregacja: sumuje wszystkie dni z wybranych dni tygodnia w zakresie. Np. „Tylko Sb" + cały maj = łączna sprzedaż wszystkich sobót w maju per przedział czasowy.
      </div>
    </div>
  );
};

export default SalesByHourTab;
